/* global debounce */

/*
 * Kalemsy, 6 Ekim 2026 (arama kutusu):
 * - İstek adresi URLSearchParams ile kurulur; boşluk "+" olur. encodeURIComponent'in "%20"si çok sözcüklü her
 *   istekte önce 302 alıyordu.
 * - Türler açıkça istenir (query, product, collection, article, page); Shopify'ın varsayılanı yazı getirmiyordu.
 *   Rehber satırları sections/predictive-search.liquid'de, ürünlerin altında en çok 2.
 * - Adında sorgu geçen ürünler üste (stoklu önce), kalanlar "Benzer ürünler" grubuna. Karşılaştırma: küçük harf,
 *   Türkçe harf ve aksan katlama (İ/ı, ü, ö, ş, ç, ğ, â), boşluk ve tire yok sayılır, virgül nokta olur; sorgunun
 *   her kelimesi başlıkta geçmeli ("uniball" = "Uni-Ball", "kuru toga" = "Kuru Toga", "kaküno" = "Kakuno").
 * - Adı eşleşen ürün yoksa aynı sorguyla 3 ürünlük ikinci istek (yalnız ürün, unavailable_products=show); adı eşleşen
 *   tükenmişler "Şu an tükendi" grubuna. İlk yanıt beklemeden çizilir; ikinci yanıt satır getirirse kutu bir kez daha
 *   çizilir. Yeni sorgu ya da kutunun kapanması eski istekleri iptal eder; eski yanıt yenisinin üstüne yazılmaz.
 * - Ok tuşları satırları DOM sırasıyla gezer (grup başlıkları ve ayrı listeler atlanır, seçilen satır görünüme kayar).
 */

if (!customElements.get('predictive-search')) {
  const KLS_TURLER = 'query,product,collection,article,page';

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.klsIstekNo = 0;
      this.klsDenetci = null;
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * Kalemsy, 6 Ekim 2026 Paket B: ürün paneli artık birden çok listeden oluşuyor (raflar, ürünler, Şu an tükendi,
     * Benzer ürünler, Rehberler, Tüm sonuçları gör); kardeş öğe yerine görünür satırların sırası kullanılır.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = Array.from(this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      )).filter((item) => !item.closest('[hidden]'));
      if (!allResults.length) return;

      const selectedIndex = selectedResult ? allResults.indexOf(selectedResult) : -1;
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = allResults[selectedIndex - 1] || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = allResults[selectedIndex + 1] || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
        if (resultToSelect.scrollIntoView) resultToSelect.scrollIntoView({ block: 'nearest' });
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      // Kalemsy, 6 Ekim 2026: adres URLSearchParams ile (boşluk "+"; "%20" 302 alıyordu).
      const searchParams = new URLSearchParams();
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams.set('q', `product_type:${this.productTypeInput.value} AND ${searchTerm}`);
      } else {
        searchParams.set('q', searchTerm);
      }
      searchParams.set('resources[type]', KLS_TURLER);
      searchParams.set('resources[limit]', theme.settings.pSearchLimit);
      searchParams.set('resources[limit_scope]', theme.settings.pSearchLimitScope);
      searchParams.set('resources[options][fields]', searchFields);
      searchParams.set('section_id', 'predictive-search');

      // Kalemsy: yeni istek öncekini iptal eder; sıra numarası tutmayan yanıt çizilmez.
      const istekNo = this.klsYeniIstek();
      const signal = this.klsDenetci ? this.klsDenetci.signal : undefined;

      const queryKey = searchParams.toString().toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const html = await this.klsBolumGetir(searchParams, signal);
        if (istekNo !== this.klsIstekNo) return;

        const resultsEl = PredictiveSearch.klsBolum(html);
        const ikinciGerekli = PredictiveSearch.klsIkinciIstekGerekli(resultsEl, searchTerm);
        PredictiveSearch.klsDuzenle(resultsEl, searchTerm, null);

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);

        // Adı eşleşen ürün yoksa tükenmişleri de içeren küçük ikinci istek; ilk yanıt beklemeden ekranda kalır.
        if (ikinciGerekli) {
          this.klsTukenenleriEkle(html, searchParams, searchTerm, queryKey, istekNo, signal);
        }
      } catch (error) {
        if (error.name === 'AbortError' || istekNo !== this.klsIstekNo) return;
        this.close();
        throw error;
      }
    }

    /**
     * Kalemsy: ikinci istek (yalnız ürün, 3 sonuç, unavailable_products=show). Adı eşleşen ürün dönerse ilk yanıt yeniden
     * düzenlenir ve kutu bir kez daha çizilir; dönmezse ya da istek düşerse ekrandaki ilk yanıt kalır.
     * @param {string} ilkHtml - İlk yanıtın metni.
     * @param {URLSearchParams} searchParams - İlk isteğin parametreleri.
     * @param {string} searchTerm - Sorgu.
     * @param {string} queryKey - Önbellek anahtarı.
     * @param {number} istekNo - İlk isteğin sıra numarası.
     * @param {AbortSignal} [signal] - İptal sinyali.
     */
    async klsTukenenleriEkle(ilkHtml, searchParams, searchTerm, queryKey, istekNo, signal) {
      const ekParams = new URLSearchParams(searchParams);
      ekParams.set('resources[type]', 'product');
      ekParams.set('resources[limit]', '3');
      ekParams.set('resources[options][unavailable_products]', 'show');

      let ekEl = null;
      try {
        ekEl = PredictiveSearch.klsBolum(await this.klsBolumGetir(ekParams, signal));
      } catch (hata) {
        return;
      }
      if (istekNo !== this.klsIstekNo) return;

      const resultsEl = PredictiveSearch.klsBolum(ilkHtml);
      if (!PredictiveSearch.klsDuzenle(resultsEl, searchTerm, ekEl)) return;

      const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
      this.cachedResults[queryKey] = resultsMarkup;
      this.renderResults(resultsMarkup);
    }

    /**
     * Kalemsy: bölümü getirir, yanıt metnini döndürür.
     * @param {URLSearchParams} params - İstek parametreleri.
     * @param {AbortSignal} [signal] - İptal sinyali.
     * @returns {Promise<string>}
     */
    async klsBolumGetir(params, signal) {
      const response = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, { signal });
      if (!response.ok) throw new Error(response.status);
      return response.text();
    }

    /**
     * Kalemsy: yanıt metninden #shopify-section-predictive-search öğesi (eylemsiz şablon içinde).
     * @param {string} html - Yanıt metni.
     * @returns {Element|null}
     */
    static klsBolum(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      return tmpl.content.querySelector('#shopify-section-predictive-search');
    }

    /**
     * Kalemsy: önceki isteği iptal eder, yeni sıra numarası verir.
     * @returns {number}
     */
    klsYeniIstek() {
      if (this.klsDenetci) this.klsDenetci.abort();
      this.klsDenetci = typeof AbortController === 'function' ? new AbortController() : null;
      this.klsIstekNo += 1;
      return this.klsIstekNo;
    }

    /**
     * Kalemsy: bekleyen isteği iptal eder (kutu kapanınca geç gelen yanıt kutuyu yeniden açmasın).
     */
    klsIptal() {
      if (this.klsDenetci) this.klsDenetci.abort();
      this.klsDenetci = null;
      this.klsIstekNo += 1;
    }

    /**
     * Kalemsy: karşılaştırma için metni sadeleştirir. Küçük harf, ı > i, aksanlar atılır (ü > u, ş > s, İ > i), virgül nokta.
     * @param {string} text - Metin.
     * @returns {string}
     */
    static klsSade(text) {
      return String(text || '')
        .toLowerCase()
        .replace(/ı/g, 'i')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/,/g, '.');
    }

    /**
     * Kalemsy: sadeleştirilmiş metinden boşluk, tire ve noktalama dışı bağlayıcıları atar ("uni-ball" > "uniball").
     * @param {string} text - Metin.
     * @returns {string}
     */
    static klsBitisik(text) {
      return PredictiveSearch.klsSade(text).replace(/[\s_/\\'’‘"“”()[\]&+:;‐-―-]/g, '');
    }

    /**
     * Kalemsy: sorgunun kelimeleri (sadeleştirilmiş, bitişik).
     * @param {string} query - Sorgu.
     * @returns {string[]}
     */
    static klsKelimeler(query) {
      return PredictiveSearch.klsSade(query)
        .split(/[\s_/‐-―-]+/)
        .map((kelime) => PredictiveSearch.klsBitisik(kelime))
        .filter(Boolean);
    }

    /**
     * Kalemsy: sorgunun her kelimesi başlığın bitişik biçiminde geçiyor mu.
     * @param {string} title - Ürün başlığı.
     * @param {string} query - Sorgu.
     * @returns {boolean}
     */
    static klsAdEslesir(title, query) {
      const kelimeler = PredictiveSearch.klsKelimeler(query);
      if (!kelimeler.length) return false;
      const baslik = PredictiveSearch.klsBitisik(title);
      return kelimeler.every((kelime) => baslik.includes(kelime));
    }

    /**
     * Kalemsy: saf ayırma. Adı eşleşenler (stoktakiler önce, kendi sıralarıyla) ve kalanlar.
     * @param {Array<{baslik: string, stokta: boolean}>} satirlar - Satırlar, Shopify'ın sırasıyla.
     * @param {string} query - Sorgu.
     * @returns {{eslesen: Array, benzer: Array}}
     */
    static klsAyir(satirlar, query) {
      const eslesen = [];
      const benzer = [];
      satirlar.forEach((satir) => {
        (PredictiveSearch.klsAdEslesir(satir.baslik, query) ? eslesen : benzer).push(satir);
      });
      return {
        eslesen: eslesen.filter((s) => s.stokta).concat(eslesen.filter((s) => !s.stokta)),
        benzer
      };
    }

    /**
     * Kalemsy: bölümdeki ürün satırları (data-kls-urun), başlık ve stok bilgisiyle.
     * @param {Element|null} root - Bölüm öğesi.
     * @returns {Array<{el: Element, baslik: string, stokta: boolean}>}
     */
    static klsSatirlar(root) {
      const liste = root ? root.querySelector('[data-kls-urunler]') : null;
      if (!liste) return [];
      return Array.from(liste.children)
        .filter((li) => li.hasAttribute('data-kls-urun'))
        .map((li) => ({
          el: li,
          baslik: (li.querySelector('.predictive-result__title') || li).textContent.trim(),
          stokta: li.getAttribute('data-kls-stok') !== '0'
        }));
    }

    /**
     * Kalemsy: ürün var ama hiçbirinin adında sorgu geçmiyorsa ikinci istek gerekir.
     * @param {Element|null} root - Bölüm öğesi.
     * @param {string} query - Sorgu.
     * @returns {boolean}
     */
    static klsIkinciIstekGerekli(root, query) {
      if (!PredictiveSearch.klsKelimeler(query).length) return false;
      const satirlar = PredictiveSearch.klsSatirlar(root);
      return satirlar.length > 0 && !satirlar.some((s) => PredictiveSearch.klsAdEslesir(s.baslik, query));
    }

    /**
     * Kalemsy: bağlantının sorgusuz yolu (iki yanıtta aynı ürünü tanımak için).
     * @param {Element} li - Satır.
     * @returns {string}
     */
    static klsYol(li) {
      const link = li.querySelector('a[href]');
      return link ? link.getAttribute('href').split('?')[0] : '';
    }

    /**
     * Kalemsy: ürün panelini gruplar. Adı eşleşenler üst listede (stoktakiler önce); ikinci yanıttan adı eşleşen
     * tükenmişler "Şu an tükendi" grubunda; üstte bir grup varsa kalanlar "Benzer ürünler" grubunda, yoksa başlıksız.
     * @param {Element|null} root - İlk yanıtın bölüm öğesi (eylemsiz şablon içinde, yerinde değişir).
     * @param {string} query - Sorgu.
     * @param {Element|null} ekRoot - İkinci yanıtın bölüm öğesi.
     * @returns {boolean} İkinci yanıttan satır eklendi mi.
     */
    static klsDuzenle(root, query, ekRoot) {
      if (!root || !PredictiveSearch.klsKelimeler(query).length) return false;
      const liste = root.querySelector('[data-kls-urunler]');
      const satirlar = PredictiveSearch.klsSatirlar(root);
      if (!liste || !satirlar.length) return false;

      const { eslesen, benzer } = PredictiveSearch.klsAyir(satirlar, query);

      const ekStokta = [];
      const ekTukendi = [];
      if (ekRoot && !eslesen.length) {
        const gorulen = new Set(satirlar.map((s) => PredictiveSearch.klsYol(s.el)));
        PredictiveSearch.klsSatirlar(ekRoot).forEach((s) => {
          if (!PredictiveSearch.klsAdEslesir(s.baslik, query)) return;
          const yol = PredictiveSearch.klsYol(s.el);
          if (gorulen.has(yol)) return;
          gorulen.add(yol);
          (s.stokta ? ekStokta : ekTukendi).push(s);
        });
      }

      const ust = eslesen.concat(ekStokta);
      ust.forEach((s) => liste.appendChild(s.el));

      const grupAc = (ad, ogeler) => {
        const grupListe = root.querySelector(`[data-kls-liste="${ad}"]`);
        const grupBaslik = root.querySelector(`[data-kls-grup="${ad}"]`);
        if (!grupListe || !grupBaslik) return false;
        ogeler.forEach((s) => grupListe.appendChild(s.el));
        grupListe.hidden = false;
        grupBaslik.hidden = false;
        return true;
      };

      if (ekTukendi.length) grupAc('tukendi', ekTukendi);

      const ustteGrupVar = ust.length > 0 || ekTukendi.length > 0;
      if (!(benzer.length && ustteGrupVar && grupAc('benzer', benzer))) {
        benzer.forEach((s) => liste.appendChild(s.el));
      }
      return ekStokta.length + ekTukendi.length > 0;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      // kls: B4. Önceki metnin gizleme zamanlayıcısı yeni metni erken gizlemesin.
      clearTimeout(this.klsDurumZaman);
      this.klsDurumZaman = setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      // Kalemsy, 6 Ekim 2026 Paket B: aria-activedescendant boş kimliğe gidiyordu; satırlara kimlik verilir.
      this.results.querySelectorAll('.predictive-search__item').forEach((item, i) => {
        if (!item.id) item.id = `${this.input.id}-secenek-${i + 1}`;
      });
      this.setAttribute('results', true);
      this.open();

      // kls: 6 Eki 2026 erişilebilirlik B4. Canlı bölge "Yükleniyor.." metninde kalıyordu; ekran okuyucu sonuç
      // geldiğini duymuyordu. Görünür öneri sayısı duyurulur.
      const klsSayi = [...this.results.querySelectorAll('.predictive-search__item')]
        .filter((item) => !item.closest('[hidden]')).length;
      this.setLiveRegionText(klsSayi
        ? `${klsSayi} öneri bulundu. Ok tuşlarıyla seçebilirsiniz.`
        : 'Öneri bulunamadı.');
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Kalemsy: bekleyen istek kapanmış kutuyu yeniden açmasın.
      this.klsIptal();

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
